import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"

import { cn } from "../../utils"

export type ProgressVariant = 'default' | 'success' | 'warning' | 'error' | 'info'

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  /** Visual variant for different states (success, warning, error) */
  variant?: ProgressVariant
  /** Size of the progress bar */
  size?: 'small' | 'default' | 'large'
  /** Whether to show animation on the progress bar */
  animated?: boolean
}

const variantStyles: Record<ProgressVariant, string> = {
  default: 'bg-primary',
  success: 'bg-status-success',
  warning: 'bg-status-warning',
  error: 'bg-destructive',
  info: 'bg-primary',
}

const sizeStyles: Record<string, string> = {
  small: 'h-1',
  default: 'h-4',
  large: 'h-6',
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, variant = 'default', size = 'default', animated, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(
      "relative w-full overflow-hidden rounded-full bg-secondary",
      sizeStyles[size] || sizeStyles.default,
      className
    )}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className={cn(
        "h-full w-full flex-1 transition-all",
        variantStyles[variant],
        animated && "animate-pulse"
      )}
      style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
    />
  </ProgressPrimitive.Root>
))
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
